/**
 * ログイン画面の処理
 */
import { STORAGE_KEYS, storage, validate } from '../../common/js/libs/utils.js';
import { setMeToLocalStorage, toast } from '../../common/js/libs/common.js';
import { showLoginReasonFromQuery } from '../../common/js/core/api/login_reason.js';
import { authApi } from './libs/apis/auth_api.js';
import { meApi } from './libs/apis/me_api.js';

let loginForm;
let loginIdInput;
let passwordInput;
let rememberCheckbox;
let loginButton;
let passwordToggle;

function onReady() {
  initializeElements();
  setupEventListeners();
  restoreLoginState();
  showLoginReasonFromQuery({ toast });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onReady);
} else {
  onReady();
}

/**
 * 要素の初期化
 */
function initializeElements() {
  loginForm = document.getElementById('login-form');
    
  loginIdInput = document.getElementById('login-id');
    
  passwordInput = document.getElementById('password');
    
  rememberCheckbox = document.querySelector('input[name="remember"]');
    
  loginButton = document.getElementById('login-button');
    
  passwordToggle = document.querySelector('.password-toggle');
}

/**
 * イベントリスナーの設定
 * フォーム送信
 * パスワード表示切り替え
 * リアルタイムバリデーション
 */
function setupEventListeners() {
  // フォーム送信
  if (loginForm) {
    loginForm.addEventListener('submit', handleSubmit);
  }
    
  // パスワード表示切り替え
  if (passwordToggle) {
    passwordToggle.addEventListener('click', togglePasswordVisibility);
  }
    
  // リアルタイムバリデーション
  if (loginIdInput) {
    loginIdInput.addEventListener('blur', validateLoginId);
  }
    
  if (passwordInput) {
    passwordInput.addEventListener('blur', validatePassword);
  }
}

/**
 * フォーム送信処理
 */
async function handleSubmit(e) {
  e.preventDefault();
    
  // バリデーション
  if (!validateForm()) {
    return;
  }
    
  // フォームデータ取得
  const formData = {
    loginId: loginIdInput.value.trim(),
    password: passwordInput.value,
    remember: rememberCheckbox.checked
  };
    
  // ログイン処理
  await performLogin(formData);
}

/**
 * ログイン処理実行
 */
async function performLogin(formData) {
  const buttonText = loginButton.querySelector('.button-text');
  const buttonLoading = loginButton.querySelector('.button-loading');
    
  // ローディング状態
  setLoadingState(true);
    
  try {
    const response = await authApi.login(formData.loginId, formData.password);
        
    // 204 No Contentの場合はログイン成功
    if (response.status === 204 || response.ok) {

      const result = await getMe();
      const me = {
        ...result.user,
        roles: result.roles,
      };
      setMeToLocalStorage(me);

      // ログイン状態を保存
      if (formData.remember) {
        storage.set(STORAGE_KEYS.USER_PREFERENCES, {
          rememberLogin: true,
          loginId: formData.loginId
        });
      } else {
        storage.remove(STORAGE_KEYS.USER_PREFERENCES);
      }
            
      // 成功メッセージ
      toast.success('ログインしました');
            
      // リダイレクト（遷移先は固定。クエリの next 等で外部サイトや javascript: へ飛ばされないようにする）
      setTimeout(() => {
        window.location.href = '/';
      }, 500);
    }
  } catch (error) {
    // エラー処理
    handleLoginError(error);
    setLoadingState(false);
  }
}


async function getMe() {
  try {
    const response = await meApi.get();
    console.debug('Success:', response);
    return response;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
}

/**
 * ログインエラー処理
 */
function handleLoginError(error) {
  let message = 'ログインに失敗しました';
    
  if (error.status === 401) {
    message = 'ログインIDまたはパスワードが正しくありません';
  } else if (error.status === 429) {
    message = 'ログイン試行回数が上限に達しました。しばらくしてから再度お試しください';
  } else if (error.message) {
    message = error.message;
  }
    
  showError(message);
}

/**
 * エラー表示
 */
function showError(message) {
  // 既存のエラーメッセージを削除
  const existingError = document.querySelector('.auth-error');
  if (existingError) {
    existingError.remove();
  }
    
  // 新しいエラーメッセージを作成
  const errorEl = document.createElement('div');
  errorEl.className = 'auth-error';
  errorEl.innerHTML = `
        <i class="fas fa-exclamation-circle"></i>
        <span>${escapeHtml(message)}</span>
    `;
    
  // フォームの先頭に挿入
  const formContainer = document.querySelector('.auth-form');
  formContainer.insertBefore(errorEl, formContainer.firstChild);
    
  // 一定時間後に自動的に削除
  setTimeout(() => {
    errorEl.style.opacity = '0';
    setTimeout(() => errorEl.remove(), 300);
  }, 5000);
}

/**
 * フォームバリデーション
 */
function validateForm() {
  let isValid = true;
    
  if (!validateLoginId()) {
    isValid = false;
  }
    
  if (!validatePassword()) {
    isValid = false;
  }
    
  return isValid;
}

/**
 * ログインIDバリデーション
 */
function validateLoginId() {
  const loginId = loginIdInput.value.trim();
  const fieldWrapper = loginIdInput.closest('.form-field');
    
  // 既存のエラーメッセージを削除
  const existingError = fieldWrapper.querySelector('.error-message');
  if (existingError) {
    existingError.remove();
  }
    
  // バリデーション
  if (!loginId) {
    showFieldError(fieldWrapper, 'ログインIDを入力してください');
    return false;
  }
    
  fieldWrapper.classList.remove('has-error');
  return true;
}

/**
 * パスワードバリデーション
 */
function validatePassword() {
  const password = passwordInput.value;
  const fieldWrapper = passwordInput.closest('.form-field');
    
  // 既存のエラーメッセージを削除
  const existingError = fieldWrapper.querySelector('.error-message');
  if (existingError) {
    existingError.remove();
  }
    
  // バリデーション
  if (!password) {
    showFieldError(fieldWrapper, 'パスワードを入力してください');
    return false;
  }
    
  fieldWrapper.classList.remove('has-error');
  return true;
}

/**
 * フィールドエラー表示
 */
function showFieldError(fieldWrapper, message) {
  fieldWrapper.classList.add('has-error');
    
  const errorEl = document.createElement('div');
  errorEl.className = 'error-message';
  errorEl.textContent = message;
    
  fieldWrapper.appendChild(errorEl);
}

/**
 * パスワード表示切り替え
 */
function togglePasswordVisibility() {
  const isPassword = passwordInput.type === 'password';
  passwordInput.type = isPassword ? 'text' : 'password';
    
  const icon = passwordToggle.querySelector('i');
  icon.className = isPassword ? 'fas fa-eye-slash' : 'fas fa-eye';
  passwordToggle.setAttribute('aria-label', isPassword ? 'パスワードを隠す' : 'パスワードを表示');
}

/**
 * ローディング状態の設定
 */
function setLoadingState(isLoading) {
  const buttonText = loginButton.querySelector('.button-text');
  const buttonLoading = loginButton.querySelector('.button-loading');
    
  loginButton.disabled = isLoading;
  buttonText.style.display = isLoading ? 'none' : 'inline-flex';
  buttonLoading.style.display = isLoading ? 'inline-flex' : 'none';
    
  // フォーム要素も無効化
  loginIdInput.disabled = isLoading;
  passwordInput.disabled = isLoading;
  rememberCheckbox.disabled = isLoading;
}

/**
 * ログイン状態の復元
 */
function restoreLoginState() {
  const savedPreferences = storage.get(STORAGE_KEYS.USER_PREFERENCES);
    
  if (savedPreferences && savedPreferences.rememberLogin) {
    loginIdInput.value = savedPreferences.loginId || '';
    rememberCheckbox.checked = true;
  }
    
  // URLパラメータからログインIDを取得（パスワードリセット後など）
  const params = new URLSearchParams(window.location.search);
  const loginId = params.get('loginId');
  if (loginId) {
    loginIdInput.value = loginId;
    passwordInput.focus();
  }
}

/**
 * HTMLエスケープ
 */
function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, m => map[m]);
}